import React from 'react'

/**
 * ResourceUsageCard component for displaying resource usage with a visual progress bar.
 *
 * Features:
 * - Displays resource name, used/total values, and percentage
 * - Visual progress bar showing percentage of limit used
 * - Color-coded based on usage severity (success/warning/danger)
 * - Supports loading state with skeleton UI
 * - Fully responsive with dark mode support
 */

interface ResourceUsageCardProps {
  /** The name/label of the resource (e.g., "Disk Space") */
  title: string
  /** Amount of resource currently used */
  used: number
  /** Total available amount of the resource */
  total: number
  /** Unit of measurement (e.g., "MB", "GB", "files") */
  unit: string
  /** Optional icon or emoji to display */
  icon?: string
  /** Loading state */
  isLoading?: boolean
  /** Color variant based on usage severity; defaults to auto-detection from percentage */
  variant?: 'success' | 'warning' | 'danger'
}

function getAutoVariant(percentage: number): 'success' | 'warning' | 'danger' {
  if (percentage >= 90) return 'danger'
  if (percentage >= 70) return 'warning'
  return 'success'
}

export const ResourceUsageCard: React.FC<ResourceUsageCardProps> = ({
  title,
  used,
  total,
  unit,
  icon,
  isLoading = false,
  variant,
}) => {
  const percentage = total > 0 ? Math.min(Math.round((used / total) * 100), 100) : 0
  const resolvedVariant = variant ?? getAutoVariant(percentage)

  const barColorClass = {
    success: 'bg-green-500 dark:bg-green-400',
    warning: 'bg-yellow-500 dark:bg-yellow-400',
    danger: 'bg-red-500 dark:bg-red-400',
  }[resolvedVariant]

  const textColorClass = {
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    danger: 'text-red-600 dark:text-red-400',
  }[resolvedVariant]

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </h3>

          {isLoading ? (
            <div className="mt-2 space-y-2">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse w-full mt-3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
            </div>
          ) : (
            <>
              <p className={`text-2xl font-bold mt-2 ${textColorClass}`}>
                {percentage}%
              </p>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-3">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${barColorClass}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {used} / {total} {unit}
              </p>
            </>
          )}
        </div>

        {icon && (
          <div className="text-2xl ml-4 opacity-75">
            {icon}
          </div>
        )}
      </div>
    </div>
  )
}
